import { useEffect, useMemo, useRef, useState } from 'react'
import { createProService, deleteProService, listProServices, updateProService, type ProService } from '../../api/proServices'

type FormState = {
  id: number | null
  name: string
  duration_minutes: string
  price_pixe: string
  capacity: string
  is_active: boolean
}

type FieldErrors = Record<string, string>

const EMPTY_FORM: FormState = {
  id: null,
  name: '',
  duration_minutes: '30',
  price_pixe: '',
  capacity: '1',
  is_active: true,
}

function sortServices(rows: ProService[]): ProService[] {
  return [...rows].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1
    return a.name.localeCompare(b.name)
  })
}

function parseApiFieldErrors(payload: any): FieldErrors {
  if (payload?.field_errors && typeof payload.field_errors === 'object') return payload.field_errors as FieldErrors
  return {}
}

function validateForm(form: FormState): FieldErrors {
  const errors: FieldErrors = {}
  const name = form.name.trim()
  const duration = Number(form.duration_minutes || 0)
  const price = Number(form.price_pixe || 0)
  const capacity = Number(form.capacity || 0)
  if (name.length < 3) errors.name = 'Name must be at least 3 characters.'
  if (!Number.isFinite(duration) || duration < 15 || duration > 480) errors.duration_minutes = 'Duration must be between 15 and 480 minutes.'
  if (!Number.isFinite(price) || price < 1) errors.price_pixe = 'Price must be at least 1 PIXE.'
  if (!Number.isFinite(capacity) || capacity < 1 || capacity > 50) errors.capacity = 'Capacity must be between 1 and 50.'
  return errors
}

export default function ProsServicesTab() {
  const [services, setServices] = useState<ProService[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; message: string } | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const nameInputRef = useRef<HTMLInputElement | null>(null)

  const activeCount = useMemo(() => services.filter((item) => item.is_active).length, [services])
  const priceApproxUsd = useMemo(() => {
    const price = Number(form.price_pixe || 0)
    if (!Number.isFinite(price) || price <= 0) return null
    return price.toFixed(2)
  }, [form.price_pixe])

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await listProServices()
      setServices(sortServices(rows))
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load services.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  useEffect(() => {
    if (!notice) return
    const timer = window.setTimeout(() => setNotice(null), 1800)
    return () => window.clearTimeout(timer)
  }, [notice])

  const resetForm = () => {
    setForm(EMPTY_FORM)
    setFieldErrors({})
  }

  const onSubmit = async () => {
    const clientErrors = validateForm(form)
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors)
      return
    }
    setSaving(true)
    setError(null)
    setNotice(null)
    setFieldErrors({})
    const payload = {
      name: form.name.trim(),
      duration_minutes: Number(form.duration_minutes),
      price_pixe: Number(form.price_pixe),
      capacity: Number(form.capacity),
      is_active: form.is_active,
    }
    try {
      if (form.id) {
        const updated = await updateProService(form.id, payload)
        setServices((prev) => sortServices(prev.map((item) => (item.id === updated.id ? updated : item))))
        setNotice({ kind: 'success', message: 'Service updated.' })
      } else {
        const created = await createProService(payload)
        setServices((prev) => sortServices([created, ...prev]))
        setNotice({ kind: 'success', message: 'Service added.' })
      }
      resetForm()
      window.setTimeout(() => nameInputRef.current?.focus(), 0)
    } catch (e: any) {
      const payloadErrors = e?.response?.data
      setFieldErrors(parseApiFieldErrors(payloadErrors))
      const message = payloadErrors?.non_field_error || payloadErrors?.detail || e?.message || 'Failed to save service.'
      setError(message)
      setNotice({ kind: 'error', message })
    } finally {
      setSaving(false)
    }
  }

  const onEdit = (service: ProService) => {
    setError(null)
    setNotice(null)
    setFieldErrors({})
    setForm({
      id: service.id,
      name: service.name,
      duration_minutes: String(service.duration_minutes),
      price_pixe: String(service.price_pixe),
      capacity: String(service.capacity || 1),
      is_active: Boolean(service.is_active),
    })
    window.setTimeout(() => nameInputRef.current?.focus(), 0)
  }

  const onToggleActive = async (service: ProService) => {
    const previous = services
    const optimistic = sortServices(services.map((item) => (item.id === service.id ? { ...item, is_active: !item.is_active } : item)))
    setServices(optimistic)
    setSaving(true)
    setError(null)
    try {
      const updated = await updateProService(service.id, { is_active: !service.is_active })
      setServices((prev) => sortServices(prev.map((item) => (item.id === updated.id ? updated : item))))
      setNotice({ kind: 'success', message: updated.is_active ? 'Service enabled.' : 'Service disabled.' })
    } catch (e: any) {
      setServices(previous)
      const message = e?.response?.data?.detail || e?.message || 'Failed to update service status.'
      setError(message)
      setNotice({ kind: 'error', message })
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async (service: ProService) => {
    if (!window.confirm(`Delete "${service.name}"? This cannot be undone.`)) return
    const previous = services
    setServices((prev) => prev.filter((item) => item.id !== service.id))
    setSaving(true)
    setError(null)
    try {
      await deleteProService(service.id)
      if (form.id === service.id) resetForm()
      setNotice({ kind: 'success', message: 'Service deleted.' })
    } catch (e: any) {
      setServices(previous)
      const message = e?.response?.data?.detail || e?.message || 'Failed to delete service.'
      setError(message)
      setNotice({ kind: 'error', message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-white">{form.id ? 'Edit service' : 'Add service'}</div>
          <div className="text-xs text-white/65">{activeCount} active</div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="text-sm md:col-span-2">Name
            <input
              ref={nameInputRef}
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className={`mt-1 w-full rounded-lg border bg-black/30 px-3 py-2 ${fieldErrors.name ? 'border-red-400/60' : 'border-white/15'}`}
            />
            {fieldErrors.name ? <div className="mt-1 text-xs text-red-300">{fieldErrors.name}</div> : null}
          </label>

          <label className="text-sm">Duration (minutes)
            <input
              type="number"
              min={15}
              max={480}
              value={form.duration_minutes}
              onChange={(e) => setForm((prev) => ({ ...prev, duration_minutes: e.target.value }))}
              className={`mt-1 w-full rounded-lg border bg-black/30 px-3 py-2 ${fieldErrors.duration_minutes ? 'border-red-400/60' : 'border-white/15'}`}
            />
            {fieldErrors.duration_minutes ? <div className="mt-1 text-xs text-red-300">{fieldErrors.duration_minutes}</div> : null}
          </label>

          <label className="text-sm">Price (PIXE)
            <input
              type="number"
              min={1}
              step={1}
              value={form.price_pixe}
              onChange={(e) => setForm((prev) => ({ ...prev, price_pixe: e.target.value }))}
              className={`mt-1 w-full rounded-lg border bg-black/30 px-3 py-2 ${fieldErrors.price_pixe ? 'border-red-400/60' : 'border-white/15'}`}
            />
            <div className="mt-1 text-[11px] text-white/55">Clients pay in PIXE.{priceApproxUsd ? ` ≈ $${priceApproxUsd}` : ''}</div>
            {fieldErrors.price_pixe ? <div className="mt-1 text-xs text-red-300">{fieldErrors.price_pixe}</div> : null}
          </label>

          <label className="text-sm">Capacity
            <input
              type="number"
              min={1}
              max={50}
              value={form.capacity}
              onChange={(e) => setForm((prev) => ({ ...prev, capacity: e.target.value }))}
              className={`mt-1 w-full rounded-lg border bg-black/30 px-3 py-2 ${fieldErrors.capacity ? 'border-red-400/60' : 'border-white/15'}`}
            />
            {fieldErrors.capacity ? <div className="mt-1 text-xs text-red-300">{fieldErrors.capacity}</div> : null}
          </label>
        </div>

        <label className="mt-3 inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
          />
          Active
        </label>

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={saving}
            className="rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Saving...' : form.id ? 'Save changes' : 'Add service'}
          </button>
          {form.id ? (
            <button type="button" onClick={resetForm} className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">
              Cancel
            </button>
          ) : null}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-sm font-semibold text-white">Services list</div>
        {loading ? <div className="mt-2 text-sm text-white/70">Loading services...</div> : null}
        {!loading && services.length === 0 ? (
          <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3 text-sm text-white/70">
            No services yet. Add your first service to appear in booking.
          </div>
        ) : null}
        {!loading && services.length > 0 ? (
          <div className="mt-3 space-y-2">
            {services.map((service) => (
              <div key={service.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm">
                <div>
                  <div className="font-semibold text-white">{service.name}</div>
                  <div className="text-xs text-white/65">
                    {service.duration_minutes} min • Cap {service.capacity || 1} • {service.price_pixe} PIXE
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] ${service.is_active ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100' : 'border-white/15 bg-white/5 text-white/70'}`}>
                    {service.is_active ? 'Active' : 'Hidden'}
                  </span>
                  <button type="button" onClick={() => onEdit(service)} className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs hover:bg-white/10">
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => void onToggleActive(service)}
                    className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs hover:bg-white/10"
                  >
                    {service.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void onDelete(service)}
                    className="rounded-lg border border-red-400/30 bg-red-500/10 px-2.5 py-1.5 text-xs text-red-200 hover:bg-red-500/20"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {notice ? (
          <div className={`mt-3 rounded-lg border p-2 text-sm ${notice.kind === 'error' ? 'border-red-400/30 bg-red-500/10 text-red-200' : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200'}`}>
            {notice.message}
          </div>
        ) : null}
        {error ? <div className="mt-2 text-sm text-red-300">{error}</div> : null}
      </div>
    </div>
  )
}

